import { Link } from '@tanstack/react-router';
import { MapPin, ListChecks, Sparkles, Cloud, ArrowRight, Globe2, ShieldCheck } from 'lucide-react';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useAuthStore } from '@/stores/auth';
import { Logo } from '@/components/Logo';
import { MapView, type MapPoint } from '@/components/map/MapView';
import { useShowcaseSites } from '@/data/dive-sites';

// Hurghada / Red Sea – islands + wrecks make for a dense, recognisable cluster.
const SHOWCASE_CENTER = { lat: 27.25, lon: 33.83 };

export function WelcomePage() {
  useDocumentTitle('Dein Tauchlogbuch online');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { data: showcase } = useShowcaseSites(SHOWCASE_CENTER, 80, 60);
  const points: MapPoint[] = (showcase?.items ?? [])
    .filter((s) => s.geoLatitude != null && s.geoLongitude != null)
    .map((s) => ({
      id:   s.id,
      lat:  s.geoLatitude!,
      lng:  s.geoLongitude!,
      tooltip: {
        title:    s.title,
        subtitle: s.regionPathStr ?? 'Red Sea, Egypt',
        badge:    s.siteType ?? undefined,
      },
    }));

  return (
    <div className="min-h-dvh bg-[var(--background)] text-[var(--foreground)]">
      {/* Top nav */}
      <header className="sticky top-0 z-30 border-b border-ocean-100/60 bg-white/80 backdrop-blur-md dark:border-ocean-800 dark:bg-ocean-950/80">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/welcome" className="flex items-center gap-2 text-ocean-900 dark:text-white">
            <Logo className="h-7 w-7" />
            <span className="text-base font-semibold tracking-tight">DiveLogApp</span>
          </Link>
          <nav className="flex items-center gap-2">
            {isAuthenticated ? (
              <Link
                to="/"
                className="inline-flex h-9 items-center gap-1 rounded-md bg-ocean-700 px-4 text-sm font-medium text-white hover:bg-ocean-600"
              >
                Zum Dashboard <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : (
              <>
                <Link to="/login" className="rounded-md px-3 py-1.5 text-sm font-medium text-ocean-700 hover:bg-ocean-50">
                  Login
                </Link>
                <Link
                  to="/register"
                  className="inline-flex h-9 items-center gap-1 rounded-md bg-ocean-700 px-4 text-sm font-medium text-white hover:bg-ocean-600"
                >
                  Kostenlos starten <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-ocean-700 via-ocean-800 to-ocean-950 text-white">
        <div aria-hidden className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-coral-500/20 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-ocean-300/20 blur-3xl" />
        <div className="relative mx-auto grid max-w-6xl gap-10 px-4 py-20 sm:px-6 md:py-28 lg:grid-cols-2 lg:items-center lg:py-32">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-ocean-100 ring-1 ring-white/20 backdrop-blur">
              <Sparkles className="h-3 w-3" /> Neu: PWA + Offline-Logging
            </span>
            <h1 className="mt-4 text-4xl font-semibold tracking-tight md:text-5xl lg:text-6xl">
              Dein Tauchlogbuch.<br />
              <span className="text-coral-300">Endlich digital.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-ocean-100 md:text-lg">
              Tauchgänge in wenigen Sekunden loggen, alle Plätze auf einer Karte, weltweit über 20.000 Sites
              durchsuchen – und neue direkt im Wizard anlegen. Auch offline auf dem Boot.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/register"
                className="inline-flex h-12 items-center gap-2 rounded-lg bg-coral-500 px-6 text-sm font-semibold text-white shadow-lg shadow-coral-500/30 transition hover:bg-coral-600"
              >
                Kostenlos starten <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/login"
                className="inline-flex h-12 items-center gap-2 rounded-lg bg-white/10 px-6 text-sm font-semibold text-white ring-1 ring-white/30 backdrop-blur transition hover:bg-white/20"
              >
                Ich hab schon ein Konto
              </Link>
            </div>
            <p className="mt-4 text-xs text-ocean-200">
              Kostenlos · Keine Kreditkarte · Daten gehören dir.
            </p>
          </div>

          {/* Hero map – real Google map of Hurghada / Red Sea dive sites,
              with the grid overlay + a sample dive-log card kept from the mock. */}
          <div className="relative">
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-2xl ring-1 ring-white/10">
              <div className="absolute inset-0">
                <MapView
                  points={points}
                  height="100%"
                  rounded={false}
                  className="h-full w-full"
                  interactive={false}
                />
              </div>

              {/* Grid overlay – stays on top of the map for the signature look. */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 grid grid-cols-6 grid-rows-4 gap-0 opacity-40"
              >
                {Array.from({ length: 24 }).map((_, i) => (
                  <div key={i} className="ring-1 ring-ocean-200/50" />
                ))}
              </div>

              {/* Sample dive-log card overlay (illustrative, not bound to a real entry). */}
              <div className="pointer-events-none absolute bottom-6 left-6 right-6 rounded-xl bg-white p-4 text-slate-900 shadow-2xl">
                <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">Letzter Tauchgang</p>
                <p className="mt-0.5 text-base font-semibold">Shaab Umm Gamar — Hurghada</p>
                <div className="mt-2 flex gap-2 text-xs">
                  <span className="rounded-full bg-ocean-50 px-2 py-0.5 font-medium text-ocean-700">21 m</span>
                  <span className="rounded-full bg-ocean-50 px-2 py-0.5 font-medium text-ocean-700">70 min</span>
                  <span className="rounded-full bg-coral-50 px-2 py-0.5 font-medium text-coral-600">★★★★★</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 md:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">Alles drin, was Tauchgänge braucht</h2>
          <p className="mt-3 text-[var(--muted-foreground)]">
            Vom ersten Open-Water bis zur Tech-Expedition – DiveLogApp wächst mit deiner Logbuch-Sammlung.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <FeatureCard
            icon={ListChecks}
            title="Wizard-Logging"
            text="Vier kurze Schritte – Wann/Wo, Tauchgang, Ausrüstung, Notizen. Werte sind valide, Bedienung ist schnell."
          />
          <FeatureCard
            icon={MapPin}
            title="Tauchplatz on-the-fly"
            text="Tauchplatz fehlt? Direkt im Logging-Flow Karte klicken, fertig. Reverse-Geocoding schlägt die Region vor."
          />
          <FeatureCard
            icon={Globe2}
            title="20.000+ Sites weltweit"
            text="Aus OpenStreetMap + DiveVibe-Community kuratiert. Mit Site-Typ, Schwierigkeit, Tiefe, Beschreibung."
          />
          <FeatureCard
            icon={Sparkles}
            title="Karte mit Cluster"
            text="Alle deine Tauchgänge auf einer Karte. Cluster-Bubbles mit Tooltip-Liste sobald sich die Pins stapeln."
          />
          <FeatureCard
            icon={Cloud}
            title="Offline auf dem Boot"
            text="PWA-installierbar. Loggen funktioniert ohne Netz – bei der nächsten Verbindung wird automatisch synchronisiert."
          />
          <FeatureCard
            icon={ShieldCheck}
            title="Deine Daten gehören dir"
            text="Selbst-gehostet, JWT-basierte Auth, Refresh-Cookies httpOnly. Export deiner Tauchgänge jederzeit möglich."
          />
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-ocean-700 to-ocean-900 text-white">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 md:py-20">
          <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
            Bereit für deinen ersten digitalen Tauchgang?
          </h2>
          <p className="mt-3 text-ocean-100">
            Konto in unter einer Minute. Kostenlos, dauerhaft.
          </p>
          <div className="mt-8">
            <Link
              to="/register"
              className="inline-flex h-12 items-center gap-2 rounded-lg bg-coral-500 px-8 text-base font-semibold text-white shadow-lg shadow-coral-500/30 transition hover:bg-coral-600"
            >
              Jetzt starten <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-ocean-100 bg-white py-8 text-center text-xs text-[var(--muted-foreground)] dark:border-ocean-800 dark:bg-ocean-950">
        © {new Date().getFullYear()}{' '}
        <a
          href="https://www.golle-it.de"
          target="_blank"
          rel="noreferrer noopener"
          className="font-medium text-ocean-700 hover:underline dark:text-ocean-200"
        >
          GOLLE Digital Solutions GmbH
        </a>
        {' · '}
        <Link to="/login" className="hover:underline">Login</Link>
      </footer>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, text }: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  text: string;
}) {
  return (
    <div className="rounded-2xl border border-[var(--border)] bg-[var(--card)] p-6 transition hover:shadow-lg hover:ring-2 hover:ring-ocean-200">
      <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-ocean-700 to-ocean-500 text-white shadow-sm">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-base font-semibold">{title}</h3>
      <p className="mt-1.5 text-sm text-[var(--muted-foreground)]">{text}</p>
    </div>
  );
}
